import { motion } from "framer-motion";

const LOGO_SRC = "/wellnet-logo.png";
const LOGO_ASPECT = "1984 / 169";

// Metallic silver fill using CSS mask + gradient
const SILVER_GRADIENT =
  "linear-gradient(180deg, #ffffff 0%, #d9d9d9 18%, #7a7a7a 42%, #f5f5f5 55%, #a8a8a8 72%, #eaeaea 88%, #6f6f6f 100%)";

function Img({ className = "", style = {} }) {
  return (
    <div
      role="img"
      aria-label="Wellnet"
      className={`block w-full ${className}`}
      style={{
        aspectRatio: LOGO_ASPECT,
        background: SILVER_GRADIENT,
        WebkitMaskImage: `url(${LOGO_SRC})`,
        maskImage: `url(${LOGO_SRC})`,
        WebkitMaskRepeat: "no-repeat",
        maskRepeat: "no-repeat",
        WebkitMaskSize: "contain",
        maskSize: "contain",
        WebkitMaskPosition: "center",
        maskPosition: "center",
        filter: "drop-shadow(0 1px 0 rgba(255,255,255,0.08))",
        ...style,
      }}
    />
  );
}

/* Each effect applies a motion treatment to the actual logo image. */
export function HeroLogoEffect({ effectId }) {
  const wrap = { display: "block", width: "100%" };

  if (effectId === "fade-blur") {
    return (
      <motion.div
        style={wrap}
        initial={{ filter: "blur(18px)", opacity: 0 }}
        animate={{ filter: "blur(0px)", opacity: 1 }}
        transition={{ duration: 1.4, ease: [0.22, 1, 0.36, 1] }}
      >
        <Img />
      </motion.div>
    );
  }

  if (effectId === "stagger") {
    // For an image we can't stagger letters; emulate with a 3-band vertical slide.
    return (
      <div style={{ ...wrap, position: "relative" }}>
        {[0, 1, 2].map((i) => (
          <motion.div
            key={i}
            style={{
              position: i === 0 ? "relative" : "absolute",
              inset: 0,
              clipPath: `inset(${(i * 100) / 3}% 0 ${100 - ((i + 1) * 100) / 3}% 0)`,
            }}
            initial={{ y: 40, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: i * 0.12, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          >
            <Img />
          </motion.div>
        ))}
      </div>
    );
  }

  if (effectId === "typewriter") {
    // Reveal logo from left to right
    return (
      <div style={{ ...wrap, overflow: "hidden" }}>
        <motion.div
          initial={{ clipPath: "inset(0 100% 0 0)" }}
          animate={{ clipPath: "inset(0 0% 0 0)" }}
          transition={{ duration: 1.4, ease: "linear" }}
        >
          <Img />
        </motion.div>
      </div>
    );
  }

  if (effectId === "shimmer") {
    return (
      <div style={{ ...wrap, position: "relative" }}>
        <Img />
        <motion.div
          aria-hidden
          style={{
            position: "absolute",
            inset: 0,
            background:
              "linear-gradient(100deg, transparent 30%, rgba(255,255,255,0.85) 50%, transparent 70%)",
            mixBlendMode: "screen",
            pointerEvents: "none",
          }}
          animate={{ x: ["-100%", "100%"] }}
          transition={{ duration: 2.8, repeat: Infinity, ease: "linear" }}
        />
      </div>
    );
  }

  // default: split-text mask reveal (slides up from below)
  return (
    <div style={{ ...wrap, overflow: "hidden" }}>
      <motion.div
        initial={{ y: "110%" }}
        animate={{ y: "0%" }}
        transition={{ duration: 1, ease: [0.76, 0, 0.24, 1] }}
      >
        <Img />
      </motion.div>
    </div>
  );
}
